import React, { useRef, useEffect, useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { getTopMentors } from "../../ApiServices/ApiServices";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const MentorCard = ({ mentor, onHover }) => {
  const [isHovering, setIsHovering] = useState({
    name: false,
    company: false,
    designation: false
  });
  const [isCardHovered, setIsCardHovered] = useState(false);

  const truncateText = (text, maxLength = 15) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const ScrollingText = ({ text, isScrolling, maxLength = 15 }) => {
    if (!text) return null;
    
    if (!isScrolling || text.length <= maxLength) {
      return <span>{truncateText(text, maxLength)}</span>;
    }

    return (
      <div className="overflow-hidden whitespace-nowrap">
        <span className="inline-block animate-scroll">
          {text}
        </span>
        <style jsx>{`
          @keyframes scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(calc(-100% + 120px)); }
          }
          .animate-scroll {
            animation: scroll 4s ease-in-out forwards;
          }
        `}</style>
      </div>
    );
  };

  const handleTextMouseEnter = (field) => {
    setIsHovering(prev => ({ ...prev, [field]: true }));
    if (!isCardHovered) {
      setIsCardHovered(true);
      onHover(true);
    }
  };

  const handleTextMouseLeave = (field) => {
    setIsHovering(prev => ({ ...prev, [field]: false }));
  };

  const handleCardMouseEnter = () => {
    if (!isCardHovered) {
      setIsCardHovered(true);
      onHover(true);
    }
  };

  const handleCardMouseLeave = () => {
    setIsCardHovered(false);
    setIsHovering({
      name: false,
      company: false,
      designation: false
    });
    onHover(false);
  };

  return (
    <NavLink
      to={`/mentors-list/details?id=${mentor._id}&name=${encodeURIComponent(mentor.name)}`}
      style={{ textDecoration: "none" }}
    >
      <div 
        className="min-w-[200px] max-w-[200px] bg-white rounded-lg shadow-md flex flex-col items-center p-4"
        onMouseEnter={handleCardMouseEnter}
        onMouseLeave={handleCardMouseLeave}
      >
        <img
          src={mentor.profileImage?.url || mentor.profileImage || "fallback.png"}
          alt={mentor.name}
          className="w-24 h-24 rounded-full mb-2 object-cover"
        />
        
        <div 
          className="w-full text-center py-1 px-2"
          onMouseEnter={() => handleTextMouseEnter('name')}
          onMouseLeave={() => handleTextMouseLeave('name')}
        >
          <h2 className="font-bold text-sm leading-tight">
            <ScrollingText 
              text={mentor.name} 
              isScrolling={isHovering.name}
              maxLength={15}
            />
          </h2>
        </div>

        <div 
          className="w-full text-center py-1 px-2"
          onMouseEnter={() => handleTextMouseEnter('company')}
          onMouseLeave={() => handleTextMouseLeave('company')}
        >
          <p className="text-sm text-gray-600">
            <ScrollingText 
              text={mentor.company || mentor.currentCompany} 
              isScrolling={isHovering.company}
              maxLength={18}
            />
          </p>
        </div>

        <div 
          className="w-full text-center py-1 px-2"
          onMouseEnter={() => handleTextMouseEnter('designation')}
          onMouseLeave={() => handleTextMouseLeave('designation')}
        >
          <p className="text-sm text-gray-600">
            <ScrollingText 
              text={mentor.designation || mentor.selectedDesignation} 
              isScrolling={isHovering.designation}
              maxLength={18}
            />
          </p>
        </div>
      </div>
    </NavLink>
  );
};

const Session = () => {
  const navigate = useNavigate();
  const topLayerRef = useRef(null);
  const bottomLayerRef = useRef(null);
  const [mentors, setMentors] = useState([]);
  const [topPaused, setTopPaused] = useState(false);
  const [bottomPaused, setBottomPaused] = useState(false);

  useEffect(() => {
    getTopMentors(30).then(setMentors);
  }, []);

  useEffect(() => {
    const topLayer = topLayerRef.current;
    const bottomLayer = bottomLayerRef.current;

    const scroll = () => {
      if (topLayer && !topPaused) {
        topLayer.scrollLeft += 1;
        if (topLayer.scrollLeft >= topLayer.scrollWidth / 2) {
          topLayer.scrollLeft = 0;
        }
      }
      
      if (bottomLayer && !bottomPaused) {
        bottomLayer.scrollLeft -= 1;
        if (bottomLayer.scrollLeft <= 0) {
          bottomLayer.scrollLeft = bottomLayer.scrollWidth / 2;
        }
      }
    };

    const interval = setInterval(scroll, 16);
    return () => clearInterval(interval);
  }, [topPaused, bottomPaused]);

  const handleTopCardHover = (isHovering) => {
    setTopPaused(isHovering);
  };

  const handleBottomCardHover = (isHovering) => {
    setBottomPaused(isHovering);
  };

  // For infinite scroll, duplicate the mentors array
  const mentorsTop = mentors;
  const mentorsBottom = mentors;

  return (
    <div className="overflow-hidden bg-red-100 px-2 py-20">
      <div className="flex flex-row sm:flex-row justify-between items-center">
        <div className="flex">
          <h1 className="text-2xl md:text-4xl font-bold my-4 sm:my-8">
            Mock Interview
          </h1>
          <button
            onClick={() => navigate("/mentors-list")}
            className="text-blue-500 hover:underline text-2xl md:text-2xl mt-2 p-2"
          >
            See All <ArrowForwardIcon />
          </button>
        </div>
      </div>

      {/* Top Scrolling Layer */}
      <div className="relative h-50">
        <div
          ref={topLayerRef}
          className="flex gap-4 w-full h-full overflow-x-scroll hide-scrollbar"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <style>
            {`
              .hide-scrollbar::-webkit-scrollbar {
                display: none;
              }
            `}
          </style>
          {/* First pass of mentors */}
          {mentorsTop.map((mentor) => (
            <MentorCard 
              key={mentor._id} 
              mentor={mentor} 
              onHover={handleTopCardHover}
            />
          ))}
          {/* Cloned pass of mentors for infinite scrolling */}
          {mentorsTop.map((mentor) => (
            <MentorCard 
              key={`clone-${mentor._id}`} 
              mentor={mentor} 
              onHover={handleTopCardHover}
            />
          ))}
        </div>
      </div>

      {/* Bottom Scrolling Layer */}
      <div className="relative h-50 mt-8 mb-8">
        <div
          ref={bottomLayerRef}
          className="flex gap-4 w-full h-full overflow-x-scroll hide-scrollbar"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <style>
            {`
              .hide-scrollbar::-webkit-scrollbar {
                display: none;
              }
            `}
          </style>
          {/* First pass of mentors */}
          {mentorsBottom.map((mentor) => (
            <MentorCard 
              key={mentor._id} 
              mentor={mentor} 
              onHover={handleBottomCardHover}
            />
          ))}
          {/* Cloned pass of mentors for infinite scrolling */}
          {mentorsBottom.map((mentor) => (
            <MentorCard 
              key={`clone-${mentor._id}`} 
              mentor={mentor} 
              onHover={handleBottomCardHover}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Session;